<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <div
        :class="
          cn(
            'tw-flex tw-h-9 tw-w-full tw-items-center tw-rounded-md tw-border tw-border-neutral-200 tw-bg-transparent tw-px-3 tw-py-1 tw-text-sm tw-shadow-sm tw-cursor-pointer focus-within:tw-ring-1 focus-within:tw-ring-neutral-950',
            props.class
          )
        "
      >
        <CalendarIcon class="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-text-neutral-400" />
        <span v-if="modelValue" class="tw-flex-1 tw-truncate">{{ formattedDate }}</span>
        <span v-else class="tw-flex-1 tw-truncate tw-text-neutral-500">{{ placeholder }}</span>
        <button
          v-if="modelValue"
          type="button"
          class="tw-ml-auto tw-h-4 tw-w-4 tw-shrink-0 tw-text-neutral-400 hover:tw-text-neutral-600"
          @click.stop="handleClear"
        >
          <X class="tw-h-3 tw-w-3" />
        </button>
      </div>
    </PopoverTrigger>
    <PopoverContent class="tw-w-auto tw-p-3" align="start">
      <Calendar v-model="internalDate" @update:model-value="handleDateChange" />
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { format } from 'date-fns';
import { CalendarIcon, X } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarDate, getLocalTimeZone, today } from '@internationalized/date';

interface Props {
  modelValue?: Date | string | null;
  placeholder?: string;
  dateFormat?: string;
  class?: any;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Pick a date',
  dateFormat: 'dd.MM.yyyy',
});

const emit = defineEmits<{
  'update:modelValue': [value: Date | null];
}>();

const isOpen = ref(false);

const internalDate = ref<any>(undefined);

const formattedDate = computed(() => {
  if (!props.modelValue) return '';
  const date = props.modelValue instanceof Date ? props.modelValue : new Date(props.modelValue);
  if (isNaN(date.getTime())) return '';
  return format(date, props.dateFormat);
});

const handleDateChange = (newDate: any) => {
  if (!newDate) {
    emit('update:modelValue', null);
    return;
  }
  const jsDate = newDate.toDate(getLocalTimeZone());
  emit('update:modelValue', jsDate);
  isOpen.value = false;
};

const handleClear = () => {
  internalDate.value = undefined;
  emit('update:modelValue', null);
};

const toCalendarDate = (date: Date | string | null | undefined): any => {
  if (!date) return undefined;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return undefined;
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

watch(
  () => props.modelValue,
  newValue => {
    internalDate.value = toCalendarDate(newValue);
  },
  { immediate: true }
);
</script>
